import { v4 as uuidv4 } from 'uuid';
import { 
  saveAnalysisSession as saveAnalysisSessionToDb, 
  getAllSessions as getAnalysisSessionsFromDb, 
  getSessionWithData as getSessionResultsFromDb, 
  deleteSessionData as deleteSessionFromDb
} from './dataService.js';

/**
 * Get or create a session ID
 * @returns {string} The session ID
 */
const getSessionId = () => {
  let sessionId = localStorage.getItem('analysis_session_id');
  if (!sessionId) {
    sessionId = uuidv4();
    localStorage.setItem('analysis_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Save analysis results to both Supabase and local storage
 * @param {Array} results - Analysis results to save
 * @param {string} sessionName - Optional name for the session
 * @returns {Promise<Object>} Result of the operation
 */
export const saveAnalysisSession = async (results, sessionName = null) => {
  try {
    // Generate a new session ID for each new analysis session
    const sessionId = uuidv4();
    const timestamp = new Date().toISOString();

    // Set this as the current active session
    localStorage.setItem('analysis_session_id', sessionId);

    // Create session data object
    const sessionData = {
      id: sessionId,
      name: sessionName || `Analysis Session ${new Date().toLocaleDateString()}`,
      timestamp,
      website_count: results.length
    };

    console.log('Saving new session with ID:', sessionId);
    console.log('Saving website analysis and openers to localStorage only');

    // Save to localStorage only for website analysis and openers
    try {
      // Save session info
      localStorage.setItem(`session_${sessionId}`, JSON.stringify(sessionData));
      
      // Save results with the session
      localStorage.setItem(`session_${sessionId}_data`, JSON.stringify({
        name: sessionName || `Analysis Session ${new Date().toLocaleDateString()}`,
        timestamp: timestamp,
        results: results.map(result => ({
          ...result,
          // Convert any Date objects to ISO strings to ensure proper serialization
          analyzedAt: result.analyzedAt ? new Date(result.analyzedAt).toISOString() : new Date().toISOString()
        }))
      }));
      
      console.log('Successfully saved session and results to localStorage');
    } catch (localError) {
      console.error('Failed to save to localStorage:', localError);
      throw localError;
    }

    return { success: true, sessionId };
  } catch (error) {
    console.error('Error in saveAnalysisSession:', error);
    return { success: false, error };
  }
};

/**
 * Get all sessions from Supabase with localStorage fallback
 * @returns {Promise<Array>} List of sessions
 */
export const getAnalysisSessions = async () => {
  try {
    const sessions = [];
    
    // Get website analysis sessions from localStorage first
    console.log('Fetching website analysis sessions from localStorage');
    
    // Scan localStorage for session data
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      // Look for session info (not the data)
      if (key && key.startsWith('session_') && !key.endsWith('_data') && !key.endsWith('_openers')) {
        try {
          const sessionData = JSON.parse(localStorage.getItem(key));
          if (sessionData) {
            const sessionId = key.replace('session_', '');
            
            // Get the results count and session type if available
            let resultCount = 0;
            let sessionType = 'website_analysis'; // default
            const resultsData = localStorage.getItem(`session_${sessionId}_data`);
            if (resultsData) {
              const parsedData = JSON.parse(resultsData);
              resultCount = parsedData.results?.length || 0;
              sessionType = parsedData.sessionType || 'website_analysis';
            }
            
            sessions.push({
              id: sessionId,
              name: sessionData.name || `Session ${sessionId.substring(0, 8)}`,
              timestamp: sessionData.timestamp || new Date().toISOString(),
              website_count: resultCount,
              session_type: sessionType,
              verified_leads: 0,
              safe_leads: 0,
              status: 'active',
              localOnly: true // Mark as local-only session
            });
          }
        } catch (parseError) {
          console.error('Error parsing session data:', parseError);
        }
      }
    }
    
    // Get email verification sessions from Supabase
    try {
      console.log('Fetching email verification sessions from Supabase');
      const dbSessions = await getAnalysisSessionsFromDb();
      if (dbSessions && dbSessions.length > 0) {
        console.log(`Found ${dbSessions.length} sessions in Supabase`);
        // Map database fields to expected format and filter for email verification sessions
        const emailSessions = dbSessions
          .filter(session => session.session_type === 'email_verification')
          .map(session => ({
            id: session.session_id,
            name: session.session_name || `Session ${session.session_id?.substring(0, 8)}`,
            timestamp: session.created_at || session.updated_at,
            website_count: session.total_leads || 0,
            session_type: session.session_type || 'email_verification',
            verified_leads: session.verified_leads || 0,
            safe_leads: session.safe_leads || 0,
            status: session.status || 'active'
          }));
        sessions.push(...emailSessions);
      }
    } catch (dbError) {
      console.warn('Failed to fetch email verification sessions from Supabase:', dbError);
    }
    
    // Sort by timestamp, most recent first
    sessions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    console.log(`Found ${sessions.length} sessions in localStorage`);
    return sessions;
  } catch (error) {
    console.error('Error in getAnalysisSessions:', error);
    return [];
  }
};

/**
 * Get results for a specific session from Supabase with localStorage fallback
 * @param {string} sessionId - The session ID
 * @returns {Promise<Array>} The session results
 */
export const getSessionResults = async (sessionId) => {
  try {
    if (!sessionId) {
      console.log('getSessionResults: No session ID provided');
      return [];
    }

    console.log('getSessionResults: Getting session results for:', sessionId);
    
    // First, try to get results from localStorage
  const localData = localStorage.getItem(`session_${sessionId}_data`);
  const openerData = localStorage.getItem(`session_${sessionId}_openers`);
  console.log('getSessionResults: Local data exists:', !!localData);
  console.log('getSessionResults: Opener data exists:', !!openerData);
  
  let results = [];
  
  // Get website analysis results
  if (localData) {
    console.log('getSessionResults: Found local session data, parsing...');
    try {
      const parsed = JSON.parse(localData);
      console.log('getSessionResults: Parsed data structure:', Object.keys(parsed));
      console.log('getSessionResults: Results array length:', (parsed.results || []).length);
      if (parsed.results && parsed.results.length > 0) {
        console.log('getSessionResults: Sample result:', parsed.results[0]);
        results = [...results, ...parsed.results];
      }
    } catch (parseError) {
      console.error('getSessionResults: Error parsing local session data:', parseError);
    }
  } else {
    console.log('getSessionResults: No local session data found');
  }
  
  // Get opener results
  if (openerData) {
    console.log('getSessionResults: Found opener data, parsing...');
    try {
      const openers = JSON.parse(openerData);
      console.log('getSessionResults: Opener array length:', openers.length);
      if (openers.length > 0) {
        console.log('getSessionResults: Sample opener:', openers[0]);
        // Convert openers to results format
        const openerResults = openers.map(opener => ({
          ...opener,
          type: 'generated_opener',
          text: opener.openerText,
          website: opener.targetWebsite
        }));
        results = [...results, ...openerResults];
      }
    } catch (parseError) {
      console.error('getSessionResults: Error parsing opener data:', parseError);
    }
  } else {
    console.log('getSessionResults: No opener data found');
  }
  
  if (results.length > 0) {
    console.log('getSessionResults: Returning', results.length, 'total results');
    return results;
  }
    
    // If no localStorage data, try Supabase as fallback (for legacy data)
    try {
      const dbResults = await getSessionResultsFromDb(sessionId);
      if (dbResults) {
        // getSessionWithData returns {session, leads, websites, openers}
        // We need to combine all the data into a results array
        const combinedResults = [];
        
        // Add verified leads as results (these should use Supabase)
        if (dbResults.leads && dbResults.leads.length > 0) {
          combinedResults.push(...dbResults.leads.map(lead => ({
            ...lead,
            type: 'verified_lead'
          })));
        }
        
        // Add website analysis as results (legacy fallback)
        if (dbResults.websites && dbResults.websites.length > 0) {
          combinedResults.push(...dbResults.websites.map(website => ({
            ...website,
            type: 'website_analysis'
          })));
        }
        
        // Add generated openers as results (legacy fallback)
        if (dbResults.openers && dbResults.openers.length > 0) {
          combinedResults.push(...dbResults.openers.map(opener => ({
            ...opener,
            type: 'generated_opener'
          })));
        }
        
        console.log('Retrieved legacy results from Supabase, found', combinedResults.length, 'total results');
        return combinedResults;
      }
    } catch (dbError) {
      console.warn('Failed to fetch from Supabase:', dbError);
    }
    
    console.log('No results found for session', sessionId);
    return [];
  } catch (error) {
    console.error('Error in getSessionResults:', error);
    return [];
  }
};

/**
 * Delete a session and its results from both Supabase and localStorage
 * @param {string} sessionId - The session ID to delete
 * @returns {Promise<Object>} Result of the operation
 */
export const deleteSession = async (sessionId) => {
  try {
    console.log('Deleting session:', sessionId);

    // Try to delete from Supabase first
    try {
      const dbResult = await deleteSessionFromDb(sessionId);
      if (dbResult) {
        console.log('Successfully deleted session from Supabase');
      }
    } catch (dbError) {
      console.warn('Failed to delete from Supabase:', dbError);
    }

    // Always remove from localStorage
    localStorage.removeItem(`session_${sessionId}`);
    localStorage.removeItem(`session_${sessionId}_data`);

    // If this was the current session, clear that too
    if (localStorage.getItem('analysis_session_id') === sessionId) {
      localStorage.removeItem('analysis_session_id');
    }

    console.log('Successfully deleted session and results');
    return { success: true };
  } catch (error) {
    console.error('Error in deleteSession:', error);
    return { success: false, error };
  }
};

/**
 * Get the current session ID
 * @returns {string|null} The current session ID
 */
export const getCurrentSessionId = () => {
  return localStorage.getItem('analysis_session_id');
};

/**
 * Set the current session ID
 * @param {string} sessionId - The session ID to set as current
 * @returns {string} The set session ID
 */
export const setCurrentSessionId = (sessionId) => {
  localStorage.setItem('analysis_session_id', sessionId);
  return sessionId;
};

/**
 * Create a new session in both Supabase and localStorage
 * @param {string} sessionName - The name for the new session
 * @returns {Promise<Object>} The new session details
 */
export const createNewSession = async (sessionName) => {
  try {
    const sessionId = uuidv4();
    const timestamp = new Date().toISOString();
    const sessionData = {
      id: sessionId,
      name: sessionName || `Analysis Session ${new Date().toLocaleDateString()}`,
      timestamp,
      website_count: 0
    };

    console.log('Creating new website analysis session:', sessionId);
    console.log('Website analysis sessions use localStorage only');

    // Save session info to localStorage only for website analysis
    localStorage.setItem(`session_${sessionId}`, JSON.stringify(sessionData));
    
    // Set as current session
    setCurrentSessionId(sessionId);

    // Initialize empty results array in local storage
    try {
      localStorage.setItem(`session_${sessionId}_data`, JSON.stringify({
        name: sessionName || `Analysis Session ${new Date().toLocaleDateString()}`,
        timestamp: timestamp,
        results: []
      }));
    } catch (localError) {
      console.error('Failed to save new session to localStorage:', localError);
    }

    console.log('Successfully created new website analysis session in localStorage');
    return { success: true, sessionId, session: sessionData };
  } catch (error) {
    console.error('Error in createNewSession:', error);
    return { success: false, error };
  }
};

/**
 * Create a new email verification session
 * @param {string} sessionName - Name for the session
 * @param {number} totalEmails - Total number of emails to verify
 * @returns {Promise<Object>} Result of the operation
 */
export const createEmailVerificationSession = async (sessionName, totalEmails = 0) => {
  try {
    const sessionId = uuidv4();
    const timestamp = new Date().toISOString();
    const sessionData = {
      id: sessionId,
      name: sessionName || `Email Verification - ${new Date().toLocaleDateString()}`,
      timestamp,
      website_count: 0,
      email_count: totalEmails
    };

    console.log('Creating new email verification session:', sessionId);

    // Try to save to Supabase first
    try {
      const dbResult = await saveAnalysisSessionToDb({
        sessionId: sessionId,
        sessionName: sessionData.name,
        sessionType: 'email_verification',
        totalLeads: totalEmails,
        verifiedLeads: 0,
        safeLeads: 0,
        status: 'active',
        metadata: { totalEmails }
      });
      
      if (dbResult) {
        console.log('Successfully created email verification session in Supabase');
      }
    } catch (dbError) {
      console.warn('Failed to create email verification session in Supabase:', dbError);
    }

    // Always save session info to localStorage as backup
    localStorage.setItem(`session_${sessionId}`, JSON.stringify(sessionData));
    
    // Set as current session
    setCurrentSessionId(sessionId);

    // Initialize empty results array in local storage
    try {
      localStorage.setItem(`session_${sessionId}_data`, JSON.stringify({
        name: sessionName || `Email Verification - ${new Date().toLocaleDateString()}`,
        timestamp: timestamp,
        results: [],
        sessionType: 'email_verification'
      }));
    } catch (localError) {
      console.error('Failed to save new email verification session to localStorage:', localError);
    }

    console.log('Successfully created new email verification session');
    return { success: true, sessionId, session: sessionData };
  } catch (error) {
    console.error('Error in createEmailVerificationSession:', error);
    return { success: false, error };
  }
};

/**
 * Update session with verification results
 * @param {string} sessionId - The session ID to update
 * @param {Object} verificationData - Verification results data
 * @returns {Promise<Object>} Result of the operation
 */
export const updateSessionWithVerificationResults = async (sessionId, verificationData) => {
  try {
    console.log('Updating session with verification results:', sessionId, verificationData);

    // Try to update in Supabase first
    try {
      const dbResult = await saveAnalysisSessionToDb({
        sessionId: sessionId,
        sessionName: null, // Don't update name
        sessionType: 'email_verification',
        totalLeads: verificationData.totalVerified || 0,
        verifiedLeads: verificationData.totalVerified || 0,
        safeLeads: verificationData.safeEmails || 0,
        status: verificationData.verificationComplete ? 'completed' : 'active',
        metadata: {
          safeEmails: verificationData.safeEmails || 0,
          unsafeEmails: verificationData.unsafeEmails || 0,
          verificationComplete: verificationData.verificationComplete || false
        }
      });
      
      if (dbResult) {
        console.log('Successfully updated session in Supabase');
      }
    } catch (dbError) {
      console.warn('Failed to update session in Supabase:', dbError);
    }

    // Update localStorage data
    try {
      const sessionDataKey = `session_${sessionId}_data`;
      const existingData = localStorage.getItem(sessionDataKey);
      
      if (existingData) {
        const sessionData = JSON.parse(existingData);
        sessionData.verificationResults = verificationData;
        sessionData.lastUpdated = new Date().toISOString();
        localStorage.setItem(sessionDataKey, JSON.stringify(sessionData));
        console.log('Updated session data in localStorage');
      }
    } catch (localError) {
      console.error('Failed to update session in localStorage:', localError);
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating session with verification results:', error);
    return { success: false, error };
  }
};

/**
 * Save generated opener data to Supabase
 * @param {Object} openerData - The opener data to save
 * @returns {Promise<Object>} Result of the operation
 */
export const saveOpenerData = async (openerData) => {
  try {
    const sessionId = getCurrentSessionId();
    if (!sessionId) {
      console.warn('No active session found for saving opener data');
      return { success: false, error: 'No active session' };
    }

    // Save opener data to local storage
    try {
      const openerId = uuidv4();
      const openerRecord = {
        id: openerId,
        sessionId: sessionId,
        openerText: openerData.text || openerData.opener,
        openerType: openerData.type || 'custom',
        targetWebsite: openerData.website,
        openerData: openerData,
        status: 'generated',
        createdAt: new Date().toISOString()
      };

      // Get existing openers for this session
      const existingOpeners = JSON.parse(localStorage.getItem(`session_${sessionId}_openers`) || '[]');
      
      // Add new opener
      existingOpeners.push(openerRecord);
      
      // Save back to localStorage
      localStorage.setItem(`session_${sessionId}_openers`, JSON.stringify(existingOpeners));
      
      console.log('Successfully saved opener to local storage');
      return { success: true, openerId };
    } catch (localError) {
      console.error('Failed to save opener to localStorage:', localError);
      return { success: false, error: 'Failed to save opener data to local storage' };
    }
  } catch (error) {
    console.error('Error in saveOpenerData:', error);
    return { success: false, error };
  }
};

/**
 * Get opener data for a session from local storage
 * @param {string} sessionId - Session ID
 * @returns {Array} Array of opener records
 */
export const getSessionOpeners = (sessionId) => {
  try {
    const openers = JSON.parse(localStorage.getItem(`session_${sessionId}_openers`) || '[]');
    return openers;
  } catch (error) {
    console.error('Error retrieving session openers:', error);
    return [];
  }
};